import React, { useEffect, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, ImagePlus } from "lucide-react";
import { imgUrl } from "@/lib/api";

/**
 * Responsive photo gallery with fullscreen lightbox.
 * - Mobile: swipeable horizontal carousel + dots + "N/total" counter
 * - Desktop: 1 large cover + up to 3 side tiles + "See all N photos" overlay
 * - Click any photo → fullscreen lightbox with Prev/Next (arrow keys + buttons)
 */
export default function HostelGallery({ images = [], priceBadge }) {
  const [openAt, setOpenAt] = useState(null); // index of the image shown in lightbox, or null
  const [mobileIdx, setMobileIdx] = useState(0);
  const total = images.length;

  const openAt0 = (i) => setOpenAt(i);
  const close = () => setOpenAt(null);
  const prev = (e) => { e?.stopPropagation?.(); setOpenAt((i) => (i === 0 ? total - 1 : i - 1)); };
  const next = (e) => { e?.stopPropagation?.(); setOpenAt((i) => (i === total - 1 ? 0 : i + 1)); };

  // Keyboard navigation
  useEffect(() => {
    if (openAt === null) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openAt, total]);

  if (total === 0) {
    return (
      <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 h-64 grid place-items-center text-slate-400 text-sm">
        No photos yet
      </div>
    );
  }

  return (
    <>
      {/* --- Mobile: swipeable strip --- */}
      <div className="md:hidden relative rounded-2xl overflow-hidden bg-slate-100">
        <div
          data-testid="mobile-gallery-strip"
          className="flex overflow-x-auto snap-x snap-mandatory scroll-smooth aspect-[4/3]"
          onScroll={(e) => {
            const w = e.currentTarget.clientWidth;
            setMobileIdx(Math.round(e.currentTarget.scrollLeft / w));
          }}
        >
          {images.map((img, i) => (
            <button
              key={i}
              type="button"
              onClick={() => openAt0(i)}
              className="shrink-0 w-full snap-center"
              data-testid={`mobile-gallery-slide-${i}`}
            >
              <img src={imgUrl(img)} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
        {/* dots + counter */}
        <div className="pointer-events-none absolute bottom-3 left-0 right-0 flex flex-col items-center gap-1">
          <div className="flex gap-1.5">
            {images.map((_, i) => (
              <span key={i} className={`w-1.5 h-1.5 rounded-full transition-all ${i === mobileIdx ? "bg-white w-4" : "bg-white/50"}`} />
            ))}
          </div>
          <div className="text-[10px] font-bold text-white bg-black/50 backdrop-blur rounded-full px-2 py-0.5">
            {mobileIdx + 1} / {total}
          </div>
        </div>
        <button
          type="button"
          onClick={() => openAt0(mobileIdx)}
          data-testid="mobile-gallery-expand"
          className="absolute top-3 right-3 bg-white/95 backdrop-blur rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1 shadow"
        >
          <ImagePlus size={12} /> {total}
        </button>
      </div>

      {/* --- Desktop: 1 cover + up to 3 tiles + "See all N photos" --- */}
      <div className="hidden md:grid grid-cols-4 gap-3 h-[420px]">
        <button
          type="button"
          onClick={() => openAt0(0)}
          data-testid="desktop-gallery-cover"
          className="col-span-2 row-span-2 rounded-2xl overflow-hidden bg-slate-100 group relative"
        >
          <img src={imgUrl(images[0])} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        </button>
        {images.slice(1, 4).map((img, i) => {
          const idx = i + 1;
          const isLast = i === 2 && total > 4;
          return (
            <button
              key={idx}
              type="button"
              onClick={() => openAt0(idx)}
              data-testid={`desktop-gallery-tile-${idx}`}
              className="rounded-2xl overflow-hidden bg-slate-100 group relative"
            >
              <img src={imgUrl(img)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              {isLast && (
                <div className="absolute inset-0 bg-black/55 grid place-items-center text-white">
                  <div className="text-center">
                    <ImagePlus size={22} className="mx-auto" />
                    <div className="text-sm font-bold mt-1">+ {total - 4} more</div>
                    <div className="text-[10px] uppercase tracking-widest opacity-90 mt-0.5">See all photos</div>
                  </div>
                </div>
              )}
            </button>
          );
        })}
        {/* If fewer than 4 photos, fill the last cell with a "See all N photos" prompt so it never looks empty */}
        {total < 4 && priceBadge}
      </div>

      {/* --- Lightbox --- */}
      <Dialog open={openAt !== null} onOpenChange={(v) => !v && close()}>
        <DialogContent
          className="max-w-6xl w-[96vw] p-0 bg-black/95 border-none"
          data-testid="gallery-lightbox"
        >
          <div className="relative flex items-center justify-center h-[80vh]">
            {openAt !== null && (
              <img
                src={imgUrl(images[openAt])}
                alt=""
                className="max-w-full max-h-full object-contain select-none"
              />
            )}
            {total > 1 && (
              <>
                <button
                  type="button"
                  onClick={prev}
                  data-testid="lightbox-prev"
                  className="absolute left-3 grid place-items-center w-11 h-11 rounded-full bg-white/10 hover:bg-white/25 text-white backdrop-blur"
                  aria-label="Previous photo"
                >
                  <ChevronLeft size={22} />
                </button>
                <button
                  type="button"
                  onClick={next}
                  data-testid="lightbox-next"
                  className="absolute right-3 grid place-items-center w-11 h-11 rounded-full bg-white/10 hover:bg-white/25 text-white backdrop-blur"
                  aria-label="Next photo"
                >
                  <ChevronRight size={22} />
                </button>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-xs font-bold bg-white/10 backdrop-blur px-3 py-1.5 rounded-full">
                  {openAt + 1} / {total}
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
